/**
 * The drawings on the spells panel's buttons.
 *
 * The panel used to be a list of names, one to a line, with the cost
 * floated to the right of each. It is a grid of pictures now (asked for on
 * 2026-09-22: "Use the latest revision of the spell svgs for the spell
 * panel. Don't keep the names alongside, put that in the tooltip. On mobile
 * make that appear with a long press"), so the twelve spells a full-grown
 * character holds are twelve squares to glance at rather than twelve lines
 * to read down.
 *
 * Which drawing a spell wants is not decided here. The server sends the key
 * on the spell itself — see Spell.Icon in spells.go for the argument, which
 * is the map view's: a table over here keyed by the spell's English name
 * would be a second list of the world's spells, right on the day it was
 * written and wrong, quietly, the first time one was added. What lives here
 * is only the other half of that split: what the key looks like. Drawing is
 * the client's business and the server does not draw.
 *
 * Every glyph is the inside of a sixteen-by-sixteen box, and the box is
 * given `fill="currentColor"` when it is built, which is the single most
 * important thing about this file. The panel's whole job is three colours —
 * green for a spell that will go off, gold for one that would go off and do
 * nothing worth the mana, red for one the server would refuse — and those
 * colours are on the button. An icon carrying a colour of its own would
 * fight them: a red button with a blue bolt on it says two things at once
 * and the player believes the wrong one. So nothing in this table names a
 * colour. `none` and `currentColor` are the only two words allowed, and a
 * test says so.
 *
 * They are drawn as blocks and plain strokes rather than as illustrations
 * because they are read at about twenty pixels: a picture of a hand holding
 * a lantern is a smudge at that size, and a cross, a star, a square in a
 * square is not. Each was reviewed against a rendering at the size it is
 * actually used.
 */

/** Where the drawing lives inside the button, and the box every glyph is cut to. */
const SVG_NS = "http://www.w3.org/2000/svg";
const BOX = "0 0 16 16";

/**
 * The glyphs by key, the key being what the server sends on the spell.
 *
 * Each value is the inner markup of the box: what a sixteen-by-sixteen
 * viewBox holds. They are markup rather than element trees because that is
 * how they were drawn and reviewed, and because the alternative — twenty
 * lines of createElementNS and setAttribute apiece — hides the picture in
 * the machinery of building it. It is a constant in this file's own source
 * and never anything a server or a player said, so there is nothing here
 * for markup to smuggle in.
 */
export const SPELL_GLYPHS: Record<string, string> = {
  // Sanctification: a grave marker, which is what is being put over the
  // pile of bones. The upright stands on a plinth so it reads as set in
  // the ground rather than as a floating cross.
  // Elbereth: a star, Varda's, the Star-kindler whose name it is
  // (2026-10-21), with four small ones at its corners.
  elbereth: '<path d="M8 .8 9.5 6.5 15.2 8 9.5 9.5 8 15.2 6.5 9.5.8 8 6.5 6.5z"/><circle cx="3" cy="3" r="1.1"/><circle cx="13" cy="3" r="1.1"/><circle cx="3" cy="13" r="1.1"/><circle cx="13" cy="13" r="1.1"/>',
  sanctify: '<rect x="7" y="1" width="2" height="12.5"/><rect x="4" y="4" width="8" height="2"/><rect x="3" y="13.5" width="10" height="1.5"/>',
  // Light: a torch — a flame, the wrapped head under it, and the haft.
  // Chosen on 2026-09-22 ("Use the torch glyph") from five drawings put
  // side by side at the sizes the panel actually uses. What it replaced
  // was a lamp throwing four rays, a block with four spokes, on the
  // reasoning that a drawn flame is a smudge at twenty pixels. That is
  // true of a flame on its own and not of this one, which is why the
  // drawing is built the way it is rather than as a flame.
  //
  // THREE THINGS MAKE IT READ SMALL, and all three were arrived at by
  // rendering it rather than by reasoning about it. The first is that
  // the flame is clear of the head, with a gap of about a pixel and a
  // half at the smallest size: joined to the head it stops being fire
  // and becomes a blade, and the first three attempts all read as a
  // spearhead for exactly that reason. The second is that the head is
  // wider than the flame's base and wider again than the haft, which is
  // the silhouette of a torch and of nothing else in the table. The
  // third is that the flame licks rather than tapers: a symmetrical
  // teardrop is a leaf or a drop of water, and the kink up its right
  // side is what a viewer reads as burning.
  //
  // It cannot carry the colour fire actually is, and should not. The
  // button under it is green, gold or red, and those three colours are
  // the panel's whole job — see the note at the top of this file.
  light: '<path d="M8 1c-.6 1.9-1.5 2.2-2.2 3.3-.5.8-.8 1.4-.8 2.1a3 3 0 0 0 6 0c0-1.3-1-2.2-1.6-3.4-.4.7-.8 1-1.2 1.1.3-1.1.1-2.1-.2-3.1z"/><rect x="4.9" y="9" width="6.2" height="2.5" rx="0.5"/><rect x="6.8" y="11.5" width="2.4" height="3.8"/>',
  // Heal: a cross, the sign everywhere for mending (2026-09-27: "add a
  // healing spell"). Nothing else in the table is one.
  heal: '<path d="M6.1 1.5h3.8v4.6h4.6v3.8H9.9v4.6H6.1V9.9H1.5V6.1h4.6z"/>',
  // Find person: a needle swinging to a bearing, which is exactly what the
  // answer is — a direction and a rough distance, never a position.
  find: '<path d="M13 3 6.5 13.5 5.6 8.4.5 7.5z"/>',
  // Levitate: a ledge with a way up off it and a way down off it, since the
  // one spell does both and the ground decides which.
  levitate: '<rect x="2" y="7.1" width="12" height="1.8"/><path d="M4.5 5.2 8 1.8l3.5 3.4" fill="none" stroke="currentColor" stroke-width="1.9"/><path d="M4.5 10.8 8 14.2l3.5-3.4" fill="none" stroke="currentColor" stroke-width="1.9"/>',
  // Provoke: four arrowheads pointing in at a point, which is the spell —
  // everything round you, turned towards you. Added on 2026-09-23 with the
  // spell ("Add a spell to draw aggro"). Solid heads rather than strokes,
  // so it reads at twenty pixels as a thing drawn in rather than a cross.
  provoke: '<rect x="6.5" y="6.5" width="3" height="3"/><path d="M8 5.4 4.8 1.2h6.4zM8 10.6l-3.2 4.2h6.4zM5.4 8 1.2 4.8v6.4zM10.6 8l4.2-3.2v6.4z"/>',
  // Challenge: a horn blown, with the sound going out of its bell, which is
  // the spell — Fingolfin sounding his horn at the gates of Angband — and
  // nothing pointing in, since unlike provoke it turns nobody. Added on
  // 2026-10-21 with the spell.
  challenge: '<path d="M1.2 11.6 9.4 6.4v6.6z"/><ellipse cx="9.4" cy="9.7" rx="1.3" ry="3.3"/><path d="M12 6.6q1.6 3.1 0 6.2M14 4.9q2.4 4.8 0 9.6" fill="none" stroke="currentColor" stroke-width="1.4"/>',
  // Blood bond: a shield, with one half of it filled.
  //
  // Asked for on 2026-09-22 ("make the icon look like a shield"), and the
  // old drawing had to go anyway: it was two blocks with a bar between
  // them, which was the right picture for a pact between two people and
  // is the wrong one for the spell now. Nothing about a blood bond is
  // symmetrical any more. One person stands under another's wounds; a
  // picture of a pair sharing something says the opposite of that.
  //
  // It is a heater shield — flat across the top, shoulders at the full
  // width of the box, curving to a point at the bottom — because that
  // outline is a shield and very little else. A round one is a coin at
  // twenty pixels and a kite is a leaf.
  //
  // THE HALF FILL IS THE WHOLE POINT OF DRAWING IT THIS WAY, and it was
  // arrived at by rendering ten shields at 20, 26 and 34 pixels rather
  // than by reasoning about them. A plain solid shield reads perfectly
  // well and says nothing but "shield", and it sat next to the
  // homecoming's solid house in the panel, which was a second lump of the
  // same weight (homecoming was a temple of separate pieces from
  // 2026-09-23, and the Eagles since 2026-10-10; the half fill is still
  // what makes this a bond). Split down the middle with the left half filled, it is
  // still unmistakably a shield at twenty pixels — the point at the
  // bottom carries it — and it says the one thing the spell does, which
  // is that a wound is divided. The split is vertical and dead centre so
  // it cannot be read as a horizontal band or a bar of heraldry.
  //
  // It says "divided" rather than "halved" on purpose, which is as well,
  // since a wound is halved only when one person is carrying: with two
  // bonders it is thirds and with three it is quarters. A picture cannot
  // count, and this one does not try.
  //
  // It carries no colour and must not. The button under it is green, gold
  // or red — see the note at the top of this file — and a shield painted
  // any colour of its own would be arguing with the one thing the panel
  // exists to say.
  bond: '<path d="M8 14.6C4.4 13.7 1.6 11.5 1.6 8V3L8 1z"/><path d="M8 1 1.6 3v5.4c0 3.5 2.7 5.7 6.4 6.6 3.7-.9 6.4-3.1 6.4-6.6V3z" fill="none" stroke="currentColor" stroke-width="1.9"/>',
  // Eagles: an eagle seen from below, wings spread and their tips
  // fingered, head up and tail fanned (2026-10-10, when the Eagles took
  // the place of homecoming and its temple). One solid piece, symmetrical
  // about the middle, so at twenty pixels it reads as a bird and not as an
  // arrow or a plane.
  eagle: '<path d="M8 3.6l.9 1.6 1.1 1.4C11.5 5 13.6 4.6 15.4 5.8l-1.6.6.9 1-2 .1.5 1-2.6-.1-1.2.9.4 3.4L8 14.4l-1.8-1.7.4-3.4-1.2-.9-2.6.1.5-1-2-.1.9-1L.6 5.8C2.4 4.6 4.5 5 6 6.6l1.1-1.4z"/>',
  // Chain lightning: the bolt, kinked twice for the two jumps.
  chain: '<path d="M9.5 1 3 8.5h3.5L5.5 15 12 7.5H8.5z"/>',
  // Fire nova: it goes off where you stand and catches a square out from
  // your feet, so it is drawn as a square front thrown off a centre — the
  // same square the world itself draws, for the same reason a ring would
  // be a lie about the corners.
  nova: '<path d="M4.2 4.2h7.6v7.6H4.2z" fill="none" stroke="currentColor" stroke-width="1.6"/><rect x="6.8" y="6.8" width="2.4" height="2.4"/><path d="M1 1l2.2 2.2M15 1l-2.2 2.2M1 15l2.2-2.2M15 15l-2.2-2.2" stroke="currentColor" stroke-width="1.8"/>',
  // Istari light: a shaft thrown out of a bright hand to one side, with
  // the glow round its source (2026-10-26).
  shaft: '<circle cx="3.2" cy="8" r="2.4"/><path d="M5.4 6.5 15.4 7.2v1.6L5.4 9.5z"/><path d="M3.2 1.6v2M3.2 12.4v2M.4 3.6l1.2 1.2M.4 12.4l1.2-1.2" stroke="currentColor" stroke-width="1.3"/>',
  // Haste: two chevrons, the fast-forward everyone already reads as speed.
  // Convince (2026-10-26): the wizard's staff, and a creature at its side
  // that has come over to it: a head and a body standing in the staff's
  // shelter, with an arc from the one to the other.
  convince: '<rect x="2.6" y="3.6" width="1.6" height="11.6"/><circle cx="3.4" cy="2.6" r="1.8"/><circle cx="11.4" cy="7.6" r="2"/><path d="M8.4 15.2c0-3.2 1.3-4.8 3-4.8s3 1.6 3 4.8z"/><path d="M6 4.6q4-3.4 6.6 0" fill="none" stroke="currentColor" stroke-width="1.3"/>',
  // Summon creature (2026-11-02): the wizard's staff, and a creature
  // rising out of a ring on the ground before it, with sparks over it.
  summon: '<rect x="2.6" y="3.6" width="1.6" height="11.6"/><circle cx="3.4" cy="2.6" r="1.8"/><ellipse cx="11.2" cy="14" rx="4" ry="1.4" fill="none" stroke="currentColor" stroke-width="1.1"/><circle cx="11.2" cy="7.4" r="1.9"/><path d="M8.4 13.6c0-2.8 1.2-4.2 2.8-4.2s2.8 1.4 2.8 4.2z"/><path d="M8 3.4l.9-.9M11.2 3.6V2.2M14.4 3.4l-.9-.9" fill="none" stroke="currentColor" stroke-width="1.1"/>',
  haste: '<path d="M2 2l5 6-5 6" fill="none" stroke="currentColor" stroke-width="2"/><path d="M8 2l5 6-5 6" fill="none" stroke="currentColor" stroke-width="2"/>',
  // The abilities a boss in a monster's form has instead of spells
  // (2026-10-26: "spells should be replaced with special abilities with
  // appropriate cooldown indication"); see bossform.go. Breath: the wave
  // widening away from the mouth in three bands, as the fire is drawn.
  breath: '<path d="M1 6.6h3v2.8H1zM5 4.6h4v6.8H5zM10 2.4h5v11.2h-5z"/>',
  // Rift: the ground bursting open round a point.
  rift: '<path d="M8 1.5 9.2 5l3.6-1.6-1.6 3.6 3.3 1.2-3.3 1.2 1.6 3.6-3.6-1.6L8 14.5 6.8 11l-3.6 1.6 1.6-3.6L1.5 8l3.3-1.2-1.6-3.6L6.8 5z"/>',
  // Whip: a handle and the lash curling out from it.
  whip: '<path d="M1.6 14.4 5.2 10.8" stroke="currentColor" stroke-width="2.6"/><path d="M5.2 10.8C9 7 3.6 4.4 7.6 2.4s6.4 1.6 6.8 5.2" fill="none" stroke="currentColor" stroke-width="1.5"/>',
  // Sweep: Sauron's mace, a haft and a spiked head, with the swing behind it.
  sweep: '<path d="M2 14 9 7" stroke="currentColor" stroke-width="2"/><circle cx="10.6" cy="5.4" r="2.6"/><path d="M10.6 1.4v1.4M14.6 5.4h-1.4M13.4 2.6l-1 1" stroke="currentColor" stroke-width="1.2"/><path d="M1.6 9.6Q2.2 4.4 6.6 2" fill="none" stroke="currentColor" stroke-width="1" stroke-dasharray="1.4 1.2"/>',
  // Shriek: a mouth and the cry going out from it.
  shriek: '<circle cx="3.6" cy="8" r="2.6"/><path d="M7.6 4.6q2 3.4 0 6.8M10.4 2.8q3 5.2 0 10.4M13.2 1.2q4 6.8 0 13.6" fill="none" stroke="currentColor" stroke-width="1.4"/>',
  // Web: spokes and one ring of strands.
  web: '<path d="M8 1v14M1 8h14M3 3l10 10M13 3 3 13" stroke="currentColor" stroke-width="1.1"/><path d="M8 4.2l2.7 1.1 1.1 2.7-1.1 2.7L8 11.8l-2.7-1.1L4.2 8l1.1-2.7z" fill="none" stroke="currentColor" stroke-width="1.1"/>',
  // Brood: any boss's call to its creatures (spiders, werewolves, fell
  // spirits), so a monster's face rather than any one kind's: an orc's,
  // ears out to the sides (up would be the boss badge's horns), slanted
  // eyes and two tusks up out of the jaw (2026-11-02). Eyes and mouth are
  // cut out of the head.
  brood: '<path fill-rule="evenodd" d="M3.6 3.8Q8 1.4 12.4 3.8L13.2 5.4 15.8 4.6 13.6 8.4Q13.4 10.6 12 12.6 8 15.8 4 12.6 2.6 10.6 2.4 8.4L.2 4.6 2.8 5.4zM4.4 6.4 7.2 7.4 6.8 8.8 4.6 8zM11.6 6.4 8.8 7.4 9.2 8.8 11.4 8zM4.8 10.2H11.2L10.6 12.4H10L9.6 10.6 9.2 12.4H6.8L6.4 10.6 6 12.4H5.4z"/>',
  // Lull: the moon, for sleep.
  lull: '<path d="M9.4 1.4a6.6 6.6 0 1 0 5.2 10.6 5.4 5.4 0 0 1-5.2-10.6z"/>',
  // Lure: a light hanging over the water, and its glow.
  lure: '<circle cx="8" cy="7" r="3.2"/><circle cx="8" cy="7" r="5.6" fill="none" stroke="currentColor" stroke-width="1"/><rect x="2" y="14" width="12" height="1.4"/>',
  // A monster's noise, for an admin playing one with /boss: a speech
  // bubble, the one drawing all of a kind's noises share.
  voice: '<path d="M2 2.4h12v8H7.2l-3.6 3.2v-3.2H2z"/>',
  // Vanish, for an admin leaving a fight (2026-10-27): a puff of smoke
  // with a star of sparks off it.
  vanish: '<circle cx="5" cy="10" r="3.2"/><circle cx="9.6" cy="9.4" r="3.8"/><circle cx="7" cy="6.6" r="2.6"/><path d="M13 1.4l.6 1.6 1.6.6-1.6.6-.6 1.6-.6-1.6-1.6-.6 1.6-.6z"/>',
};

/**
 * The drawing for a spell's icon key, or null when there is none.
 *
 * Null is a perfectly ordinary answer and the caller must have something to
 * do with it. A spell can reach a client that has no glyph for it in two
 * quite normal ways: a spell added to the world before its drawing is added
 * here, and a page an old proxy is still serving to somebody who has just
 * reconnected to a newer server. Neither is a reason to put an empty square
 * on the panel, so the panel falls back to the spell's own name — see
 * showSpells.
 *
 * The box carries `fill="currentColor"`, which is how a glyph takes the
 * colour of the button it is standing on, and `aria-hidden`, because the
 * button already says what it is: the name and the cost are on it as a
 * label for anything that reads rather than looks.
 */
export function spellGlyph(icon: string | undefined): SVGSVGElement | null {
  const inner = icon ? SPELL_GLYPHS[icon] : undefined;
  if (!inner) return null;
  const svg = document.createElementNS(SVG_NS, "svg") as SVGSVGElement;
  svg.setAttribute("viewBox", BOX);
  svg.setAttribute("fill", "currentColor");
  svg.setAttribute("aria-hidden", "true");
  svg.innerHTML = inner;
  return svg;
}
